import Cookies from 'js-cookie'
import get from 'lodash/get'
import type { BusinessData } from 'types/business'
import type { User } from 'types/user'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import type { MappedEventTypes } from '@disco/constants/EventTypes'
import { getEventDescription } from '@disco/constants/EventTypes'
type IsOpenCallback = (isOpen: boolean) => void
type AvailableCallback = () => void
type ShutdownCallback = () => void
type Options = {
  isSupportMenuFeatureEnabled?: boolean
}

// The widget script may not be loaded yet (e.g. right after a page reload), in which
// case Intercom('shutdown') is a no-op and the identified session cookie survives.
const removeSessionCookies = () => {
  // React Native has no window.location or document cookies to remove
  if (__REACT_NATIVE__) return

  const hostParts = window.location.hostname.split('.')
  const parentDomains = hostParts
    .slice(0, -1)
    .map((_, index) => hostParts.slice(index).join('.'))

  Object.keys(Cookies.get())
    .filter(name => name.startsWith('intercom-session-'))
    .forEach(name => {
      Cookies.remove(name, { path: '/' })
      parentDomains.forEach(domain =>
        Cookies.remove(name, { path: '/', domain }),
      )
    })
}

export default class Intercom {
  static userInitialized: boolean = false
  static isOpenCallbacks: IsOpenCallback[] = []
  static availableCallbacks: AvailableCallback[] = []
  static shutdownCallbacks: ShutdownCallback[] = []

  static setup(
    user: User | null | undefined,
    company: BusinessData | null | undefined,
    options: Options = {},
  ) {
    if (window.Intercom) {
      const companyInfo =
        user && !user.is_support_user && company
          ? {
              id: company.id,
              name: company.name,
              business_url: window.host,
              subscription_status: get(
                company,
                'private_metadata.plan_type',
                'trial',
              ),
              created_at: Math.round(new Date(2016, 1, 1).getTime() / 1000),
              ...company.stats,
            }
          : undefined

      if (user && company) {
        window.Intercom('boot', {
          app_id: company.intercom_app_id || 'idkf9hdp',
          email: user.email,
          user_id: user.id,
          user_hash: user.intercom_hash,
          current_business_id: company.id,
          sentry: `https://sentry.io/organizations/disco/issues/?project=62659&&query=is%3Aunresolved+user%3Aid%3A${user.id}`,
          version: FRONTEND_VERSION,
          ...user.stats,
          company: companyInfo,
          widget: {
            activator: '#IntercomDefaultWidget',
          },
          hide_default_launcher: options?.isSupportMenuFeatureEnabled,
        })
      } else {
        window.Intercom('boot', {
          app_id: 'idkf9hdp',
          widget: {
            activator: '#IntercomDefaultWidget',
          },
        })
      }

      window.Intercom('onShow', () =>
        Intercom.isOpenCallbacks.forEach(cb => cb(true)),
      )
      window.Intercom('onHide', () =>
        Intercom.isOpenCallbacks.forEach(cb => cb(false)),
      )
      if (!Intercom.userInitialized) {
        Intercom.userInitialized = true
        Intercom.availableCallbacks.forEach(cb => cb())
      }
    }
  }

  static send(event: { event: MappedEventTypes }): void {
    Intercom.sendRaw(getEventDescription(event.event))
  }

  static sendRaw(eventName: string): void {
    // console.log('Intercom.sendRaw', eventName)
    if (!window.Intercom) return
    window.Intercom('trackEvent', eventName)
  }

  static shutdown() {
    if (window.Intercom) window.Intercom('shutdown')
    removeSessionCookies()
    if (Intercom.userInitialized) {
      Intercom.userInitialized = false
      Intercom.shutdownCallbacks.forEach(cb => cb())
    }
  }

  static showNewMessage(message?: string | null | undefined) {
    if (window.Intercom) window.Intercom('showNewMessage', message)
  }

  static update() {
    if (window.Intercom && Intercom.userInitialized) window.Intercom('update')
  }

  static isAvailable(): boolean {
    return window.Intercom && window.Intercom.booted && Intercom.userInitialized
  }

  static isAvailableWebsite(): boolean {
    return window.Intercom && window.Intercom.booted
  }

  static openHome() {
    window.Intercom('showSpace', 'home')
  }

  static hideWidget() {
    window.Intercom('hide')
  }

  static addIsOpenCallback(callback: IsOpenCallback) {
    Intercom.isOpenCallbacks.push(callback)
  }

  static removeIsOpenCallback(callback: IsOpenCallback) {
    Intercom.isOpenCallbacks = Intercom.isOpenCallbacks.filter(
      cb => cb !== callback,
    )
  }

  static addAvailableCallback(callback: AvailableCallback) {
    Intercom.availableCallbacks.push(callback)
  }

  static removeAvailableCallback(callback: AvailableCallback) {
    Intercom.availableCallbacks = Intercom.availableCallbacks.filter(
      cb => cb !== callback,
    )
  }

  static addShutdownCallback(callback: ShutdownCallback) {
    Intercom.shutdownCallbacks.push(callback)
  }

  static removeShutdownCallback(callback: ShutdownCallback) {
    Intercom.shutdownCallbacks = Intercom.shutdownCallbacks.filter(
      cb => cb !== callback,
    )
  }
}
